import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type ToastTone = 'info' | 'success' | 'danger';

export interface ToastMessage {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastContextValue {
  toasts: readonly ToastMessage[];
  push: (toast: Omit<ToastMessage, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_ICON = {
  info: Info,
  success: CheckCircle2,
  danger: AlertTriangle,
} as const;

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'border-primary-border bg-primary-soft text-brand',
  success: 'border-success-border bg-success-soft text-success',
  danger: 'border-danger-border bg-danger-soft text-danger',
};

const AUTO_DISMISS_MS = 4500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<ToastMessage, 'id'>) => {
      const id = crypto.randomUUID();
      setToasts((current) => [...current, { ...toast, id }]);
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside a ToastProvider');
  return context;
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: readonly ToastMessage[];
  onDismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-5 right-5 z-[60] flex w-[340px] max-w-[calc(100vw-40px)] flex-col gap-2"
      role="region"
      aria-label="Notificaciones"
    >
      {toasts.map((toast) => {
        const Icon = TONE_ICON[toast.tone];
        return (
          <div
            key={toast.id}
            role="status"
            className={cn(
              'pointer-events-auto flex animate-kainos-fade-in items-start gap-2.5 rounded-xl border p-3.5 shadow-raised',
              TONE_CLASS[toast.tone],
            )}
          >
            <Icon size={16} className="mt-px shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold">{toast.title}</div>
              {toast.description ? (
                <div className="mt-0.5 text-[12px] font-medium text-ink-body">
                  {toast.description}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              aria-label="Descartar"
              className="shrink-0 text-ink-faint transition-colors hover:text-ink-body"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
